import type { Edge, Node } from '@xyflow/react'
import { isImageReferenceAssetHandle } from './dataflow'
import type { FlowNodeData } from './types'

const GENERATOR_NODE_TYPES = new Set(['generate_image', 'generate_video', 'generate_voice_template', 'generate_voice'])

/** Stale Propagation (P0 §16–§18/§23/§28–§30), generalizing P1's
 * image-asset-only `markDownstreamGeneratorsStale`.
 *
 * Any upstream material change — Replace on an `image_asset`
 * (`onChangeEntity`), an edited `text_asset`'s `text` field
 * (`onChangeFields`), or an input edge being REMOVED (§23 "删除输入连线
 * 后...节点应该标记...需要重新生成") — must mark every downstream
 * generation node that already has a result as `needsRegeneration:
 * true`, and NEVER touch/clear the existing result itself (§17 "禁止自
 * 动重新生成" — staleness is the only side effect, ever). Runs off the
 * live edge graph, not a fixed `sourceHandle` allowlist (P1's version
 * only matched `sourceHandle === 'image-out'`, silently missing the new
 * `text-out`/`video-out`/generic-unnamed-handle cases this round
 * introduces), so it now covers every one of this round's new upstream
 * material types without the caller needing to know which one fired. */
export function markDownstreamStale(nodes: Node[], edges: Edge[], sourceNodeId: string): Node[] {
  const downstreamIds = new Set(
    edges
      // VistoeraTASK-00144 — `isImageReferenceAssetHandle` matches the
      // bare generic id AND `generate_image`'s new `::<edgeId>`-scoped
      // variant, so replacing/editing an upstream asset still marks a
      // scoped-handle downstream `generate_image` node stale.
      .filter((e) => e.source === sourceNodeId && (isImageReferenceAssetHandle(e.targetHandle) || !e.targetHandle))
      .map((e) => e.target),
  )
  if (downstreamIds.size === 0) return nodes

  return nodes.map((n) => {
    if (!downstreamIds.has(n.id)) return n
    const data = n.data as unknown as FlowNodeData
    const isGenerator = GENERATOR_NODE_TYPES.has(data.nodeType) || data.nodeType === 'generate_text'
    if (!isGenerator) return n
    const fields = data.fields || {}
    const hasImageResult = !!fields.generatedOutputId
    const hasVideoResult = !!fields.generatedVideoUrl
    const textResult = fields.currentResult as { text?: string } | null | undefined
    const hasTextResult = Boolean(textResult && typeof textResult.text === 'string' && textResult.text.length)
    if (!hasImageResult && !hasVideoResult && !hasTextResult) return n
    if (fields.needsRegeneration) return n
    return {
      ...n,
      data: {
        ...data,
        fields: {
          ...fields,
          needsRegeneration: true,
        },
      } as unknown as Record<string, unknown>,
    }
  })
}

/** Targeted single-node variant of the same rule (§23) — used when an
 * INPUT EDGE ITSELF is removed (`CanvasBoard.tsx`'s `onEdgesChange`/
 * `onDisconnectMaterial`), where the caller already knows exactly which
 * ONE node lost an input and there is no "source" to fan out
 * downstream from; marks that single node `needsRegeneration: true`
 * iff it already has a result, same non-destructive guarantee as
 * `markDownstreamStale` above (never touches/clears the result). */
export function markNodeStaleIfHasResult(nodes: Node[], nodeId: string): Node[] {
  return nodes.map((n) => {
    if (n.id !== nodeId) return n
    const data = n.data as unknown as FlowNodeData
    const isGenerator = GENERATOR_NODE_TYPES.has(data.nodeType) || data.nodeType === 'generate_text'
    if (!isGenerator) return n
    const fields = data.fields || {}
    const hasImageResult = !!fields.generatedOutputId
    const hasVideoResult = !!fields.generatedVideoUrl
    const textResult = fields.currentResult as { text?: string } | null | undefined
    const hasTextResult = Boolean(textResult && typeof textResult.text === 'string' && textResult.text.length)
    if (!hasImageResult && !hasVideoResult && !hasTextResult) return n
    if (fields.needsRegeneration) return n
    return {
      ...n,
      data: {
        ...data,
        fields: {
          ...fields,
          needsRegeneration: true,
        },
      } as unknown as Record<string, unknown>,
    }
  })
}

/** @deprecated kept as a thin alias — P1's `onChangeEntity` call site
 * name — so this file's rename to a general-purpose helper doesn't also
 * force an unrelated diff in every caller in the same pass. */
export const markDownstreamGeneratorsStale = markDownstreamStale
